import { useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { getContent, getLatestArticle } from '../../sdk/services/contentService';
import ParticipantService from '../../sdk/services/participantService';
import TeamService from '../../sdk/services/teamService';
import { decryptString, formatDateDMYYYY, LoggerService } from '../../ui/utils';
import NewsCard from '../../ui/components/NewsCard/NewsCard';
import ProgressStep from '../../ui/components/ProgressStep/ProgressStep';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  ButtonWidth,
  Checkbox,
  MLPVersion,
  Modal,
  TextAlign,
  TextArea,
  Theme,
  useBreakpoint,
} from '../../ui/components';
import ArticleCarousel from '../../ui/components/ArticleCarousel/ArticleCarousel';
import { DynamicObjectInterface } from '../../sdk/type';
import { useAPICall } from '../../hooks';
import AcknowledgementService from '../../sdk/services/acknowlegementService';
import Error from '../../assets/image/icons/ico_error.svg';
import { useAdminToast } from '../../ui/components/AdminToast/AdminToast';
import { getRoundedNumber } from '../../ui/utils/common';
import { AcknowledgePayload, DashboardProps, UserData } from '../dashboard/dashboardConfig';
import { REACT_APP_AMWAYNOW } from '../../envVars';
import styles from './Homepage.module.scss';

export interface TeamData {
  totalRecords: number;
}

interface HomepageContentType {
  banner_image: {
    url: string;
  };
  banner_image_mobile: {
    url: string;
  };
  news_announcements: {
    title: string;
    description: string;
    rich_text_description: string;
  }[];
  amway_now_logo: {
    url?: string;
  };
}

export const Homepage = (props: DashboardProps) => {
  const { decodedData, campaignData, index } = props;
  const apiCall = useAPICall();
  const navigate = useNavigate();
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm'].indexOf(viewPortType) === -1;
  const [data, setData] = useState<TeamData[]>([]);
  const [content, setContent] = useState<HomepageContentType>();
  const [showAcknowledgment, setShowAcknowledgment] = useState(false);
  const [isWeightInAcknowledged, setIsWeightInAcknowledged] = useState(false);
  const [isWeightOutAcknowledged, setIsWeightOutAcknowledged] = useState(false);
  const [article, setArticle] = useState([]);
  const [loadingArticle, setLoadingArticle] = useState(true);
  const [confirmCheck, setConfirmCheck] = useState(true);
  const [formData, setFormData] = useState<UserData | null>(JSON.parse(JSON.stringify(data)) as UserData);
  const cacheKey = 'homepageContent';
  const { t } = useTranslation();
  const ipadDimensions = useMediaQuery('(max-width:960px) and (min-width:768px)');
  const { adminToast } = useAdminToast();

  const articleCacheKey = 'latestArticles';
  useEffect(() => {
    const fetchPreLoginContent = async () => {
      try {
        const data = await getContent('introduction_page', cacheKey, true);
        setContent(data);
        const numberOfArticleShow = data?.latest_articles?.article_count ?? 9;
        const categoryUid = data?.latest_articles?.article_category[0]?.uid;
        const subCategoryUid = data?.latest_articles?.article_subcategory[0]?.uid;

        let article = await getLatestArticle(articleCacheKey, true, numberOfArticleShow, categoryUid, subCategoryUid);

        article = article.map((item: any) => {
          return {
            imageSrc: item?.thumbnail?.thumbnail_image?.url,
            label: item?.article_category_reference[0]?.title,
            categoryColor: item?.article_category_reference[0]?.category_s_color,
            title: item?.display_title,
            date: item.article_date ? moment(item?.article_date).format('DD/MM/YYYY') : null,
            redirectUrl: REACT_APP_AMWAYNOW + item?.url,
            // views: item.views ?? 100, //currently remove views
          };
        });

        setArticle(article);
        setLoadingArticle(false);
      } catch (error) {
        LoggerService.log('fetchPreLoginContent:Failed to retrieve content data. Using default content.');
      }
    };

    fetchPreLoginContent();
  }, []);

  const fetchData = async (campaignData: DynamicObjectInterface) => {
    const latestCampaign = campaignData?.find((campaign: DynamicObjectInterface) =>
      campaign.enrollment?.some((enrollment: DynamicObjectInterface) => enrollment.enrollmentType === 'PARTICIPANT'),
    );

    if (latestCampaign) {
      const resParticipant = await apiCall(
        ParticipantService.getParticipantDetail,
        {
          campaignId: latestCampaign?.campaignId,
          teamId: latestCampaign?.enrollment.find(
            (enrollment: DynamicObjectInterface) => enrollment.enrollmentType === 'PARTICIPANT',
          )?.teamId,
          userId: decodedData.userId,
        },
        decodedData.userId,
      );

      let _data: UserData = resParticipant.data.data;
      _data = {
        ...resParticipant?.data?.data,
        weightIn: {
          ..._data?.weightIn,
          bodyFat: _data?.weightIn?.bodyFat
            ? getRoundedNumber(parseFloat(decryptString(`${_data?.weightIn?.bodyFat}`)))
            : 0,
          height: _data?.weightIn?.height
            ? getRoundedNumber(parseFloat(decryptString(`${_data?.weightIn?.height}`)))
            : 0,
          muscleKilogram: _data?.weightIn?.muscleKilogram
            ? getRoundedNumber(parseFloat(decryptString(`${_data?.weightIn?.muscleKilogram}`)))
            : 0,
          musclePercentage: _data?.weightIn?.musclePercentage
            ? getRoundedNumber(parseFloat(decryptString(`${_data?.weightIn?.musclePercentage}`)))
            : 0,
          weight: _data?.weightIn?.weight
            ? getRoundedNumber(parseFloat(decryptString(`${_data?.weightIn?.weight}`)))
            : 0,
        },
        weightOut: {
          ..._data?.weightOut,
          bodyFat: _data?.weightOut?.bodyFat
            ? getRoundedNumber(parseFloat(decryptString(`${_data?.weightOut?.bodyFat}`)))
            : null,
          height: _data?.weightOut?.height
            ? getRoundedNumber(parseFloat(decryptString(`${_data?.weightOut?.height}`)))
            : null,
          muscleKilogram: _data?.weightOut?.muscleKilogram
            ? getRoundedNumber(parseFloat(decryptString(`${_data?.weightOut?.muscleKilogram}`)))
            : null,
          musclePercentage: _data?.weightOut?.musclePercentage
            ? getRoundedNumber(parseFloat(decryptString(`${_data?.weightOut?.musclePercentage}`)))
            : null,
          weight: _data?.weightOut?.weight
            ? getRoundedNumber(parseFloat(decryptString(`${_data?.weightOut?.weight}`)))
            : null,
        },
      };

      setFormData(_data);
    }
  };

  useEffect(() => {
    (async () => {
      if (campaignData?.[index]?.campaignId && decodedData && index !== undefined) {
        const result = await apiCall(ParticipantService.getTeamListByUser, {
          campaignId: campaignData?.[index]?.campaignId,
          loginId: decodedData?.loginId,
          userId: decodedData?.userId,
          coachId:
            props.callFrom !== 'coachList'
              ? props?.campaignData?.[index]?.enrollment?.find(
                  (i: DynamicObjectInterface) => i.enrollmentType === 'COACH',
                )?.coachId
              : props.coachId,
          enrollmentStartDate: props.enrollmentStartDate,
          enrollmentEndDate: props.enrollmentEndDate,
          maxLength: 7,
        });

        const dataWithDetails = await Promise.all(
          result?.data?.data?.map(async (team: DynamicObjectInterface) => {
            const teamDetails = await apiCall(TeamService.getTeamDetails, `${team.teamId}`);
            const isGroupAchieved = teamDetails?.data?.data?.isGroupAchieved;

            const totalPoints = team?.participants
              ?.filter((participant: DynamicObjectInterface) => participant.status === 'ACTIVE')
              ?.map((participant: DynamicObjectInterface) => participant.point)
              ?.flatMap((item: DynamicObjectInterface) => Object.values(item))
              ?.reduce((acc: number, value: number) => acc + value, 0)
              ?.toFixed(2);

            const activeParticipants = team.participants.filter(
              (participant: DynamicObjectInterface) => participant.status === 'ACTIVE',
            );

            const isAllPurchase =
              //activeParticipants.length > 1 &&
              activeParticipants.filter((participant: DynamicObjectInterface) => participant?.purchase?.length > 0)
                .length === activeParticipants.length;

            const isAllWeightIn =
              //activeParticipants.length > 1 &&
              activeParticipants.filter((participant: DynamicObjectInterface) => !!participant?.weightIn?.weightInDate)
                .length === activeParticipants.length;

            const isAllWeightOut =
              //activeParticipants.length > 1 &&
              activeParticipants.filter((participant: DynamicObjectInterface) => !!participant.weightOut?.weightOutDate)
                .length === activeParticipants.length;

            // const isAllWin =
            //   activeParticipants.filter(
            //     (participant: DynamicObjectInterface) => !!participant.weightOutDiffValues?.result,
            //   ).length === activeParticipants.length;

            const isAllWin =
              //activeParticipants.length > 1 &&
              activeParticipants.filter((participant: DynamicObjectInterface) => participant.isIndividualAchieved === 1)
                .length === activeParticipants.length;

            const isAllSubscribe =
              //activeParticipants.length > 1 &&
              activeParticipants.filter((participant: DynamicObjectInterface) => participant.subscribe?.length > 0)
                .length === activeParticipants.length;

            const isAllValidSubscribe =
              //activeParticipants.length > 1 &&
              activeParticipants.filter(
                (participant: DynamicObjectInterface) => participant.subscribe[0]?.orderStatus === 1,
              ).length === activeParticipants.length;

            const isAllValidPurchase =
              //activeParticipants.length > 1 &&
              activeParticipants.filter(
                (participant: DynamicObjectInterface) => participant?.purchase[0]?.orderStatus === 1,
              ).length === activeParticipants.length;

            // Find the participant with the matching userId
            const matchingParticipant = team.participants.find(
              (participant: DynamicObjectInterface) => participant.userId === decodedData.userId,
            );

            // Extract the desired data from the matching participant
            const purchase = matchingParticipant?.purchase ?? null;
            const subscribe = matchingParticipant?.subscribe ?? null;
            const weightIn = matchingParticipant?.weightIn ?? null;
            const weightOut = matchingParticipant?.weightOut ?? null;
            const weightOutDiffValues = matchingParticipant?.weightOutDiffValues ?? null;
            const isIndividualAchieved = matchingParticipant?.isIndividualAchieved ?? null;
            const isIndividualTeam = teamDetails?.data?.data?.isIndividualTeam ?? null;
            const myTeam = matchingParticipant?.status === 'ACTIVE' ? true : false;
            const displayName =
              isIndividualTeam === 1
                ? activeParticipants.length > 0
                  ? activeParticipants[0]?.name
                  : 'Individual'
                : team.teamName;

            return {
              ...team,
              coachId: team.coachId,
              coachName: team.coachName,
              teamStartDate: team.teamStartDate,
              teamEndDate: team.teamEndDate,
              teamSize: team.teamSize,
              teamId: team.teamId,
              teamStatus: team.status,
              teamName: displayName,
              teamPoint: totalPoints,
              isAllPurchase: isAllPurchase,
              isAllWeightIn: isAllWeightIn,
              isAllWeightOut: isAllWeightOut,
              isAllWin: isAllWin,
              isAllSubscribe: isAllSubscribe,
              isAllValidPurchase: isAllValidPurchase,
              isAllValidSubscribe: isAllValidSubscribe,
              isGroupAchieved: isGroupAchieved,
              campaignId: campaignData?.[index]?.campaignId,
              isCoachEnrollment: campaignData?.[index]?.enrollment.some(
                (i: DynamicObjectInterface) => i.enrollmentType === 'COACH' || i.enrollmentType === 'LEADER',
              ),
              purchase,
              subscribe,
              weightIn,
              weightOut,
              weightOutDiffValues,
              isIndividualAchieved,
              isIndividualTeam,
              myTeam,
            };
          }),
        );

        setData(dataWithDetails);
        await fetchData(campaignData);
      }
    })();
  }, [campaignData, decodedData, index]);

  const handleViewAllClick = () => {
    navigate(`/campaigns/${campaignData[0].campaignId}/participants`);
  };

  const participantValidity = campaignData?.[0]?.configJson?.activities?.participant?.validity?.value
    ? parseInt(campaignData?.[0]?.configJson?.activities?.participant?.validity?.value)
    : null;
  const postAssessmentReminderDays = campaignData?.[0]?.configJson?.postAssessmentReminderDays
    ? campaignData?.[0]?.configJson?.postAssessmentReminderDays
    : null;
  const calendarLink = campaignData?.[0]?.configJson?.campaignTextLink
    ? campaignData?.[0]?.configJson?.campaignTextLink
    : 'https://calendly.com/';

  const handleRedirect = () => {
    window.open(calendarLink, '_blank');
  };

  const handleCoachEnrollment = () => {
    navigate('/coachEnrollment');
  };

  const acknowledgeParticipant = async () => {
    const payload: AcknowledgePayload[] = [];

    if (isWeightInAcknowledged) {
      payload.push({
        userId: decodedData.userId,
        campaignId: formData?.campaign?.campaignId,
        action: 'WEIGHT_IN',
        remarks: formData?.weightIn?.remarks ?? '',
        name: decodedData.name,
        teamId: formData?.teamId,
      });
    }

    if (isWeightOutAcknowledged) {
      payload.push({
        userId: decodedData.userId,
        campaignId: formData?.campaign?.campaignId,
        action: 'WEIGHT_OUT',
        remarks: formData?.weightOut?.remarks ?? '',
        name: decodedData.name,
        teamId: formData?.teamId,
      });
    }

    const acknowledgeResponse = await apiCall(AcknowledgementService.acknowledge, `${decodedData.userId}`, payload);

    if (acknowledgeResponse.status === 200) {
      setShowAcknowledgment(false);

      adminToast({
        message: t('admin.dashboard.acknowledgeSucsess'),
        theme: Theme.SUCCESS,
      });
      await fetchData(campaignData);
    } else {
      adminToast({
        message: t('admin.teamEdit.acknowledgeError'),
        theme: Theme.ERROR,
      });
    }
  };

  const acknowledgment = (
    <>
      {showAcknowledgment && (
        <Modal
          primaryButtonLabel={''}
          headline={t('admin.teamEdit.headline')}
          closeIcon={false}
          containerWidth={ipadDimensions ? '750px' : '1000px'}
          textAlign={TextAlign.LEFT}
          primaryButton={
            <Button
              title={t('admin.userSetup.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                acknowledgeParticipant();
                setShowAcknowledgment(false);
              }}
              disabled={confirmCheck}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.userSetup.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowAcknowledgment(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        >
          <div className={styles['participant-confirm-model']}>
            <div className={styles['input-row']}>
              <div className={styles['input-container']}>
                <div className={styles['participant-weight-details']}>Pre-Assessment Measurement</div>
                <table>
                  <tbody>
                    <tr>
                      <td>Name</td>
                      <td className={styles['colon-gap-name']}>:</td>
                      <td>{formData?.name}</td>
                    </tr>
                    <tr>
                      <td>Date</td>
                      <td className={styles['colon-gap-date']}>:</td>
                      <td>
                        {formData?.weightIn?.weightedDate
                          ? formatDateDMYYYY(new Date(formData.weightIn.weightedDate))
                          : '-'}
                      </td>
                    </tr>
                    <tr>
                      <td>Height</td>
                      <td className={styles['colon-gap-height']}>:</td>
                      <td>{formData?.weightIn?.height || '-'}</td>
                    </tr>
                    <tr>
                      <td>Weight</td>
                      <td className={styles['colon-gap-body-weight']}>:</td>
                      <td>{formData?.weightIn?.weight || '-'}</td>
                    </tr>
                    <tr>
                      <td>Body Fat</td>
                      <td className={styles['colon-gap-body-fat']}>:</td>
                      <td>{formData?.weightIn?.bodyFat || '-'}</td>
                    </tr>
                    <tr>
                      <td>Muscle Mass (kg)</td>
                      <td className={styles['colon-gap-muscle-kg']}>:</td>
                      <td>{formData?.weightIn?.muscleKilogram || '-'}</td>
                    </tr>
                    <tr>
                      <td>Muscle Mass (%)</td>
                      <td className={styles['colon-gap-muscle-kg1']}>:</td>
                      <td>{formData?.weightIn?.musclePercentage || '-'}</td>
                    </tr>
                    <tr>
                      <td>Remarks (if any)</td>
                      <td className={styles['colon-gap-remarks']}>:</td>
                      <td>
                        <TextArea
                          resize={false}
                          mlpVersion={MLPVersion.ONE}
                          placeholder={'Remarks'}
                          value={formData?.weightIn?.remarks ?? ''}
                          onChange={e => {
                            handleOnChange(e.target.value, 'weightIn', 'remarks');
                          }}
                          disabled={!isWeightInAcknowledged}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className={styles['input-container']}>
                <div className={styles['participant-weight-details']}>Post-Assessment Measurement</div>
                <table>
                  <tbody>
                    <tr>
                      <td>Name</td>
                      <td className={styles['colon-gap-name']}>:</td>
                      <td>{formData?.name}</td>
                    </tr>
                    <tr>
                      <td>Date</td>
                      <td className={styles['colon-gap-date']}>:</td>
                      <td>
                        {formData?.weightOut?.weightedDate
                          ? formatDateDMYYYY(new Date(formData.weightOut.weightedDate))
                          : '-'}
                      </td>
                    </tr>
                    <tr>
                      <td>Height</td>
                      <td className={styles['colon-gap-height']}>:</td>
                      <td>{formData?.weightOut?.height || '-'}</td>
                    </tr>
                    <tr>
                      <td>Weight</td>
                      <td className={styles['colon-gap-body-weight']}>:</td>
                      <td>{formData?.weightOut?.weight || '-'}</td>
                    </tr>
                    <tr>
                      <td>Body Fat</td>
                      <td className={styles['colon-gap-body-fat']}>:</td>
                      <td>{formData?.weightOut?.bodyFat || '-'}</td>
                    </tr>
                    <tr>
                      <td>Muscle Mass (kg)</td>
                      <td className={styles['colon-gap-muscle-kg']}>:</td>
                      <td>{formData?.weightOut?.muscleKilogram || '-'}</td>
                    </tr>
                    <tr>
                      <td>Muscle Mass (%)</td>
                      <td className={styles['colon-gap-muscle-kg1']}>:</td>
                      <td>{formData?.weightOut?.musclePercentage || '-'}</td>
                    </tr>
                    <tr>
                      <td>Remarks (if any)</td>
                      <td className={styles['colon-gap-remarks']}>:</td>
                      <td>
                        <TextArea
                          resize={false}
                          mlpVersion={MLPVersion.ONE}
                          placeholder={'Remarks'}
                          value={formData?.weightOut?.remarks ?? ''}
                          onChange={e => {
                            handleOnChange(e.target.value, 'weightOut', 'remarks');
                          }}
                          disabled={!isWeightOutAcknowledged}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className={styles['input-row']}>
              <div className={styles['checkbox-container']}>
                <div className={styles['confirmation-checkbox']}>
                  <Checkbox text="" checked={!confirmCheck} onChange={() => setConfirmCheck(!confirmCheck)} />
                  <div className={styles['confirmation-text']}>I verify that the information above is accurate.</div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );

  const handleConfirmDetails = () => {
    setShowAcknowledgment(true);
    if (formData?.weightIn?.weightedDate && !formData?.weightIn?.acknowledgeBy) {
      setIsWeightInAcknowledged(true);
    }
    if (formData?.weightOut?.weightedDate && !formData?.weightOut?.acknowledgeBy) {
      setIsWeightOutAcknowledged(true);
    }
    if (
      formData?.weightIn?.weightedDate &&
      !formData?.weightIn?.acknowledgeBy &&
      formData?.weightOut?.weightedDate &&
      !formData?.weightOut?.acknowledgeBy
    ) {
      setIsWeightInAcknowledged(true);
      setIsWeightOutAcknowledged(true);
    }
  };

  const handleOnChange = (value: DynamicObjectInterface, key: keyof UserData, ...paths: string[]) => {
    setFormData(prev => {
      if (!prev) return { ...prev! };

      // Explicitly specify the return type for setDeep
      const setDeep = (obj: DynamicObjectInterface, i = 0): DynamicObjectInterface => {
        if (i < paths.length) {
          const path = i === 0 ? key : paths[i];
          return {
            ...obj,
            [path]: setDeep(obj[path] || {}, i + 1),
          };
        } else {
          return { ...obj, [paths[i - 1]]: value };
        }
      };

      return paths.length > 0 ? (setDeep(prev) as UserData) : { ...prev, [key]: value };
    });
  };

  return (
    <div className={styles['homepage-container']}>
      {decodedData.isPendingCoach && (
        <div className={`${styles['dashboard-container-acknowledgement']}`}>
          <div className={styles['details-card-wrapper']}>
            <div className={styles['details-container']}>
              <div className={`${styles['details-info']} ${styles['details-info-text']}`}>
                <img src={Error} alt={`beforeImg`} width={'32px'} height={'32px'} className={styles['warning']} />
                Reminder: Kindly complete your enrolment before starting your coaching journey. Without enrolment, participants may not be able to select you as their coach.
              </div>
              <div className={styles['confirm-button']}>
                <Button
                  title="Enrol as Coach"
                  type={ButtonTypes.BUTTON}
                  size={ButtonSize.SMALL}
                  buttonStyle={ButtonStyles.OUTLINE}
                  onClick={() => handleCoachEnrollment()}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {(formData?.weightIn?.weightedDate && !formData?.weightIn?.acknowledgeBy) ||
      (formData?.weightOut?.weightedDate && !formData?.weightOut?.acknowledgeBy) ? (
        <div className={`${styles['dashboard-container-acknowledgement']}`}>
          <div className={styles['details-card-wrapper']}>
            <div className={styles['details-container']}>
              <div className={`${styles['details-info']} ${styles['details-info-text']}`}>
                <img src={Error} alt={`beforeImg`} width={'32px'} height={'32px'} className={styles['warning']} />
                The Amway Brand Experience Team has updated your{' '}
                {formData?.weightIn?.weightedDate && !formData?.weightIn?.acknowledgeBy
                  ? 'Pre-Assessment'
                  : formData?.weightOut?.weightedDate && !formData?.weightOut?.acknowledgeBy
                    ? 'Post-Assessment'
                    : ''}{' '}
                Measurement. Please click "Confirm Details" to verify the updated information.
              </div>
              <div className={styles['confirm-button']}>
                <Button
                  title="Confirm Details"
                  type={ButtonTypes.BUTTON}
                  size={ButtonSize.SMALL}
                  buttonStyle={ButtonStyles.OUTLINE}
                  onClick={() => handleConfirmDetails()}
                />
              </div>
            </div>
          </div>
        </div>
      ) : formData?.weightIn?.weightedDate &&
        !formData?.weightOut?.weightedDate &&
        participantValidity &&
        postAssessmentReminderDays &&
        new Date(
          new Date(formData?.weightIn?.weightedDate).getTime() +
            (participantValidity - postAssessmentReminderDays) * 24 * 60 * 60 * 1000,
        ).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0) ? (
        <div className={`${styles['dashboard-container-acknowledgement']}`}>
          <div className={styles['details-card-wrapper']}>
            <div className={styles['details-container']}>
              <div className={`${styles['details-info']} ${styles['details-info-text']}`}>
                <img src={Error} alt={`beforeImg`} width={'32px'} height={'32px'} className={styles['warning']} />
                Reminder: It's almost time to do your post-assessment! Please ensure this is completed with your details
                updated by{' '}
                {formatDateDMYYYY(
                  new Date(
                    new Date(formData?.weightIn?.weightedDate).getTime() + participantValidity * 24 * 60 * 60 * 1000,
                  ),
                )}
                .
              </div>
              <div className={styles['confirm-button']}>
                <Button
                  title="Book Appointment"
                  type={ButtonTypes.BUTTON}
                  size={ButtonSize.SMALL}
                  buttonStyle={ButtonStyles.OUTLINE}
                  onClick={() => handleRedirect()}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {content && (
        <>
          <div className={styles['banner']}>
            <img
              src={isDesktopView ? content.banner_image.url : content.banner_image_mobile.url}
              alt={'banner_image'}
            />
          </div>
          <div className={styles['news-container']}>
            <div className={styles['title']}>News & Announcements</div>
            <div className={styles['news-container-list']}>
              {content.news_announcements.map((contents, index) => (
                <div key={index} className={styles['news-card-wrapper']}>
                  <NewsCard data={contents} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {data?.length > 0 && (
        <div className={styles['content']}>
          <div className={styles['title']}>{campaignData[0]?.campaignName}</div>
          {/* map progressStep */}
          <div className={styles['progress-step-container']}>
            {data?.slice(0, 4).map((details: DynamicObjectInterface, index) => {
              return (
                <div className={styles['progress-step-card']} key={index}>
                  <ProgressStep
                    {...(details as DynamicObjectInterface)}
                    handleRedirect={
                      details.isIndividualTeam === 1 && details.myTeam
                        ? () => navigate(`/campaigns/${details?.campaignId}`)
                        : details.isIndividualTeam === 1 && details.participants[0]?.userId
                          ? () =>
                              navigate(
                                `/campaigns/${details?.campaignId}/teams/${details?.teamId}/user?id=${details.participants[0].userId}`,
                              )
                          : () => navigate(`/campaigns/${details?.campaignId}/teams/${details?.teamId}`)
                    }
                  ></ProgressStep>
                </div>
              );
            })}
          </div>
          {campaignData[0]?.enrollment?.some(
            (i: DynamicObjectInterface) => i.enrollmentType === 'COACH' || i.enrollmentType === 'LEADER',
          ) &&
            data?.[0]?.totalRecords > 3 && (
              <Button
                size={ButtonSize.MEDIUM}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                buttonStyle={ButtonStyles.OUTLINE}
                onClick={handleViewAllClick}
                className={styles['view-all-button']}
                title={'View all >'}
              />
            )}
        </div>
      )}
      {!loadingArticle && article?.length > 0 && (
        <ArticleCarousel logoUrl={content?.amway_now_logo?.url} cards={article} isMobile={!isDesktopView} />
      )}
      {acknowledgment}
    </div>
  );
};
